"use client";

import {
  Box,
  Button,
  ButtonProps,
  Center,
  Container,
  HStack,
  Heading,
  Spinner,
  Stack,
  colors,
} from "@biblioteksentralen/react";
import { ReactNode } from "react";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { useFacetAndFilterContext } from "./FacetAndFilterContext";
import OldSearchInfoBox from "./OldSearchInfoBox";
import { SearchCategory, useSearchContext } from "./SearchContext";
import { workSearchCategories } from "./types";
import WorkSearchFilters from "./WorkSearchFilters";

export const SearchView = () => {
  const { t } = useTranslation();
  const {
    searchState,
    setSearchState,
    workSearch,
    agentSearch,
    yourLibrarySearch,
    areSearchResultsLoading,
    areCategoriesLoading,
  } = useSearchContext();
  const { clearFacetCaches } = useFacetAndFilterContext();

  const allCategoryResults = [
    ...(workSearch?.categoryResults ?? []),
    ...(yourLibrarySearch?.categoryResults ?? []),
    ...(agentSearch?.categoryResults ?? []),
  ];

  const CurrentCategoryResults = allCategoryResults.find((category) => searchState?.selectedCategory === category.id)
    ?.children ?? (
    <Stack spacing="4rem">
      {workSearch?.summary}
      {yourLibrarySearch?.summary}
      {agentSearch?.summary}
    </Stack>
  );

  const setPage = (page: SearchCategory | undefined) => {
    setSearchState({ selectedCategory: page, work: undefined, yourLibrary: undefined, agent: undefined });
  };

  return (
    <Container maxWidth="container.lg" margin="2rem auto" padding="0 1rem" position="relative">
      <Stack spacing="1rem">
        <Heading size="lg" as="h1">
          {t("Resultat for: «{searchQuery}»", { searchQuery: searchState?.s })}
          {!allCategoryResults.length && !areSearchResultsLoading && t(" ga null treff")}
        </Heading>
        {!areCategoriesLoading && (
          <HStack flexWrap="wrap" spacing={{ base: "0.5rem", md: "1rem" }}>
            {allCategoryResults.length > 0 && (
              <FilterButton
                onClick={() => {
                  clearFacetCaches();
                  setPage(undefined);
                }}
                size={{ base: "sm", md: "md" }}
                isDisabled={searchState?.selectedCategory === undefined}
              >
                {t("Alle")}
              </FilterButton>
            )}

            {allCategoryResults.map((category) => (
              <FilterButton
                key={category.id}
                onClick={() => {
                  clearFacetCaches();
                  setPage(category.id);
                }}
                size={{ base: "sm", md: "md" }}
                isDisabled={category.id === searchState?.selectedCategory}
              >
                {category.label}
              </FilterButton>
            ))}
          </HStack>
        )}
        {workSearchCategories.find((workSearchCategory) => workSearchCategory === searchState?.selectedCategory) && (
          <Box as="section">
            <WorkSearchFilters />
          </Box>
        )}
        {areCategoriesLoading || areSearchResultsLoading ? (
          <Center padding="2rem">
            <Spinner size="xl" thickness="4px" />
          </Center>
        ) : (
          <Stack spacing="6rem">
            {CurrentCategoryResults}
            <OldSearchInfoBox />
          </Stack>
        )}
      </Stack>
    </Container>
  );
};

type FilterbuttonProps = {
  onClick: () => void;
  isDisabled: boolean;
  children: ReactNode;
} & ButtonProps;

const FilterButton = ({ onClick, children, isDisabled, ...buttonProps }: FilterbuttonProps) => (
  <Button
    onClick={onClick}
    _hover={isDisabled ? {} : { filter: "brightness(0.9)" }}
    backgroundColor={colors.grey10}
    color={colors.black}
    isDisabled={isDisabled}
    _disabled={{ background: colors.black, color: colors.white, cursor: "not-allowed" }}
    borderRadius="xl"
    fontSize="md"
    fontWeight="semibold"
    transition="0.3s ease all"
    {...buttonProps}
  >
    {children}
  </Button>
);
